import React, { useMemo } from 'react';
import { TextWithPointer } from '@common-fe/common-fe';
import _ from 'lodash';
import styled from 'styled-components';

import { REGEXPS } from '@/common';
import { TransactionStatus, TransactionStatusKeys } from '@/modules/transaction/transaction.types';

interface StatusProps {
  null?: boolean;
  processing?: boolean;
  pending?: boolean;
  waitingForFunds?: boolean;
  needsHelp?: boolean;
  paid?: boolean;
  partiallyPaid?: boolean;
  denied?: boolean;
  repaid?: boolean;
  expired?: boolean;
  complete?: boolean;
  declined?: boolean;
  customHeight?: string;
  reopen?: boolean;
}

export const Status = styled.p<StatusProps>`
  ${({ theme }) => theme.fonts.text14Medium};
  height: ${({ customHeight }) => customHeight || '34px'};
  padding: 0px 0px;
  padding-left: 19px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  /* justify-content: center; */
  box-sizing: border-box;
  text-align: left;
  position: relative;
  &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    left: 0;
    ${({
      theme,
      processing,
      pending,
      waitingForFunds,
      complete,
      reopen,
      needsHelp,
      paid,
      partiallyPaid,
      denied,
      repaid,
      expired,
      declined,
    }) => {
      const colors = theme.colors.statusColors;
      let color = colors.lockedNull;
      if (processing) {
        color = colors.processingPending;
      }
      if (pending) {
        color = colors.processingPending;
      }
      if (needsHelp || waitingForFunds) {
        color = colors.needsHelp;
      }
      if (paid || complete) {
        color = colors.paidActiveComplete;
      }
      if (partiallyPaid) {
        color = colors.partiallyPaid;
      }
      if (denied) {
        color = colors.deniedErrorExpired;
      }
      if (repaid) {
        color = colors.paidActiveComplete;
      }
      if (expired) {
        color = colors.deniedErrorExpired;
      }
      if (declined) {
        color = colors.declined;
      }
      if (reopen) {
        color = colors.reopen;
      }
      return {
        'background-color': color,
      };
    }}
  }
`;

interface Props {
  status?: string;
}

const ActivityStatus: React.FC<Props> = ({ status }) => {
  const text = useMemo(() => {
    return _.capitalize(status || '').replace(REGEXPS.DASH_SYMBOL, ' ');
  }, [status]);

  if (status === TransactionStatusKeys.Processing || status === TransactionStatus.Processing) {
    return (
      <Status processing data-testid="ActivityStatus-processing-wrap">
        {text}
      </Status>
    );
  }
  if (status === TransactionStatusKeys.NeedsHelp || status === TransactionStatus.NeedsHelp) {
    return (
      <Status needsHelp data-testid="ActivityStatus-needshelp-wrap">
        {text}
      </Status>
    );
  }
  if (
    status === TransactionStatusKeys.Paid ||
    status === TransactionStatus.Paid ||
    status === TransactionStatusKeys.PaidIneligibleExpense ||
    status === TransactionStatus.PaidIneligibleExpense
  ) {
    return (
      <Status paid data-testid="ActivityStatus-paid-wrap">
        <TextWithPointer ellipsisMode title={text}>
          {text}
        </TextWithPointer>
      </Status>
    );
  }
  if (status === TransactionStatusKeys.OutOfPocket || status === TransactionStatus.OutOfPocket) {
    return (
      <Status paid data-testid="ActivityStatus-paid-wrap">
        {text}
      </Status>
    );
  }
  if (
    status === TransactionStatusKeys.AppliedToDeductible ||
    status === TransactionStatus.AppliedToDeductible
  ) {
    return (
      <Status paid data-testid="ActivityStatus-paid-wrap">
        {text}
      </Status>
    );
  }
  if (status === TransactionStatusKeys.PartiallyPaid || status === TransactionStatus.PartiallyPaid) {
    return (
      <Status partiallyPaid data-testid="ActivityStatus-partiallypaid-wrap">
        {text}
      </Status>
    );
  }
  if (status === TransactionStatusKeys.WaitingForFunds || status === TransactionStatus.WaitingForFunds) {
    return (
      <Status waitingForFunds data-testid="ActivityStatus-waitingforfunds-wrap">
        {text}
      </Status>
    );
  }
  if (status === TransactionStatusKeys.Pending || status === TransactionStatus.Pending) {
    return (
      <Status pending data-testid="ActivityStatus-pending-wrap">
        {text}
      </Status>
    );
  }
  if (status === TransactionStatusKeys.Repaid || status === TransactionStatus.Repaid) {
    return (
      <Status repaid data-testid="ActivityStatus-repaid-wrap">
        {text}
      </Status>
    );
  }
  if (status === TransactionStatusKeys.Denied || status === TransactionStatus.Denied) {
    return (
      <Status denied data-testid="ActivityStatus-denied-wrap">
        {text}
      </Status>
    );
  }
  if (status === TransactionStatusKeys.Expired || status === TransactionStatus.Expired) {
    return (
      <Status expired data-testid="ActivityStatus-expired-wrap">
        {text}
      </Status>
    );
  }
  if (status === TransactionStatusKeys.Complete || status === TransactionStatus.Complete) {
    return (
      <Status complete data-testid="ActivityStatus-complete-wrap">
        {text}
      </Status>
    );
  }
  if (status === TransactionStatusKeys.Declined || status === TransactionStatus.Declined) {
    return (
      <Status denied data-testid="ActivityStatus-complete-wrap">
        {text}
      </Status>
    );
  }
  if (status === TransactionStatusKeys.RepaymentRequired || status === TransactionStatus.RepaymentRequired) {
    return (
      <Status denied data-testid="ActivityStatus-repaymentRequired-wrap">
        {text}
      </Status>
    );
  }
  if (status === TransactionStatusKeys.Voided || status === TransactionStatus.Voided) {
    return (
      <Status denied data-testid="ActivityStatus-voided-wrap">
        {text}
      </Status>
    );
  }
  if (
    status === TransactionStatusKeys.AccountVerificationFailed ||
    status === TransactionStatus.AccountVerificationFailed
  ) {
    return (
      <Status expired data-testid="ActivityStatus-accountVerificationFailed-wrap">
        {text}
      </Status>
    );
  }
  if (
    status === TransactionStatusKeys.PendingAccountVerification ||
    status === TransactionStatus.PendingAccountVerification
  ) {
    return (
      <Status pending data-testid="ActivityStatus-pendingAccountVerification-wrap">
        <TextWithPointer ellipsisMode title={text}>
          {text}
        </TextWithPointer>
      </Status>
    );
  }
  if (status === TransactionStatusKeys.PartiallyRepaid || status === TransactionStatus.PartiallyRepaid) {
    return (
      <Status partiallyPaid data-testid="ActivityStatus-PartiallyRepaid-wrap">
        {text}
      </Status>
    );
  }
  if (status === TransactionStatus.RepaymentInitiated) {
    return (
      <Status pending data-testid="ActivityStatus-repayment-initiated-wrap">
        {text}
      </Status>
    );
  }
  return (
    <Status null data-testid="ActivityStatus-null-wrap">
      Null
    </Status>
  );
};

export default ActivityStatus;
